import React, { useState } from 'react';
import axios from 'axios';
import {
  UploadCloud, CheckCircle, AlertCircle, Info,
  ShieldCheck, Database, Download, FileSpreadsheet,
  ArrowRight, Sparkles
} from 'lucide-react';
import * as XLSX from 'xlsx';

const ComboVehicleEntry = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [showToast, setShowToast] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setStatus({ type: '', message: '' });
  };

  const downloadSampleExcel = () => {
    const sampleData = [
      {
        "Vehicle No": "MP13AB1234", "Owner": "John Doe", "Mobile": "9876543210",
        "Chassis": "MA3JKL2S...", "Tax Validity": "2025-12-31",
        "Fitness Validity": "2025-10-15", "Insurance Expiry": "2025-08-20"
      }
    ];
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample_Format");
    XLSX.writeFile(workbook, "Vehicle_Import_Format.xlsx");
  };

  const handleUpload = async () => {
    if (!file) {
      setStatus({ type: 'error', message: 'Please select a file first!' });
      return;
    }
    const formData = new FormData();
    formData.append('excel_file', file);
    setLoading(true);
    try {
      const response = await axios.post('http://127.0.0.1:8000/api/combo-upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStatus({ type: 'success', message: response.data.message || "Data Sync Successful!" });
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      setFile(null);
      if (document.getElementById('fileInput')) document.getElementById('fileInput').value = '';
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.error || "Server Connection Failed!" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={mainWrapper}>
      {/* --- STYLES --- */}
      <style>{`
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        .unified-content-box {
          animation: slideUp 0.6s ease-out;
          display: grid; 
          grid-template-columns: 1fr 1px 1.2fr;
          width: 100%;
          background: #d8d5d5;
          border-radius: 24px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.04);
        }

        .premium-drop-zone {
          transition: all 0.3s ease;
          border: 2px dashed #cbd5e1;
          background: #fdfdfe;
          border-radius: 20px;
          padding: 45px 20px;
          text-align: center;
          position: relative;
        }
        .premium-drop-zone:hover {
          border-color: #3b82f6;
          background: #f0f7ff;
        }

        .btn-sync {
          background: #1e293b;
          color: white;
          border: none;
          padding: 16px;
          border-radius: 14px;
          font-weight: 700;
          font-size: 15px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
        }
        .btn-sync:hover:not(:disabled) {
          background: #0f172a;
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(0,0,0,0.1);
        }

        .btn-sample {
          background: #eff6ff;
          color: #3b82f6;
          border: 1px solid #3B82F6;
          padding: 14px;
          border-radius: 12px;
          font-weight: 700;
          cursor: pointer;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: 0.2s;
        }
        .btn-sample:hover {
          background: #3b82f6;
          color: white;
        }

        @media (max-width: 850px) {
          .unified-content-box { grid-template-columns: 1fr; }
          .vertical-divider { display: none; }
        }
      `}</style>

      {/* --- UI CONTENT --- */}
      <div className="unified-content-box">

        {/* Left: Info & Guidelines */}
        <div style={leftPanel}>
          <div style={header}>
            <div style={iconBox}><Database size={20} color="#fff" /></div>
            <h2 style={title}>Bulk Data Sync</h2>
          </div>

          <div style={guideBody}>
            <div style={infoBadge}><Sparkles size={12} /> Data Engine v2</div>
            <h4 style={subTitle}>Upload Guidelines</h4>
            <ul style={listStyle}>
              <li>Ensure headers match: <b>Vehicle No, Owner, Mobile, Chassis, Tax Validity, Fitness Validity, Insurance Expiry</b></li>
              <li>Date format: <b>YYYY-MM-DD</b></li>
              <li>Max records per batch: <b>5000 rows</b></li>
              <li>Supported format: <b>.xlsx / .xls</b></li>
            </ul>
          </div>

          <button onClick={downloadSampleExcel} className="btn-sample">
            <Download size={18} /> Download Template
          </button>

          <div style={securityNote}>
            <ShieldCheck size={14} color="#64748b" />
            <span>Encrypted validation enabled</span>
          </div>
        </div>

        {/* Divider */}
        <div className="vertical-divider" style={dividerStyle}></div>

        {/* Right: Upload Section */}
        <div style={rightPanel}>
          <div style={uploadContainer}>
            <div style={uploadHeader}>
              <div style={circleIcon}><FileSpreadsheet size={28} color="#3b82f6" /></div>
              <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', fontWeight: '800' }}>Select File</h3>
              <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Commit your excel data to database</p>
            </div>

            <div className="premium-drop-zone">
              <input id="fileInput" type="file" accept=".xlsx, .xls" onChange={handleFileChange} style={hiddenInput} />
              <div style={{ pointerEvents: 'none' }}>
                <UploadCloud size={30} color="#cbd5e1" style={{ marginBottom: '10px' }} />
                <h5 style={{ margin: '0 0 5px 0', color: file ? '#1e293b' : '#64748b' }}>
                  {file ? file.name : "Click to Browse"}
                </h5>
                <p style={{ fontSize: '11px', color: '#94a3b8' }}>Excel Files only (Max 10MB)</p>
              </div>
            </div>

            {status.message && (
              <div style={{
                ...statusBox,
                background: status.type === 'success' ? '#f0fdf4' : '#fef2f2',
                color: status.type === 'success' ? '#166534' : '#991b1b',
                border: `1px solid ${status.type === 'success' ? '#bbf7d0' : '#fecaca'}`
              }}>
                {status.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {status.message}
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={loading}
              className="btn-sync"
              style={{ marginTop: status.message ? '0' : '20px' }}
            >
              {loading ? 'Processing...' : 'Start Import'}
              {!loading && <ArrowRight size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- STYLES OBJECTS ---
const mainWrapper = { width: '100%', padding: '0' };
const leftPanel = { padding: '40px', display: 'flex', flexDirection: 'column', gap: '20px' };
const rightPanel = { padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#fafbfc' };

const header = { display: 'flex', alignItems: 'center', gap: '12px' };
const iconBox = { background: '#3b82f6', padding: '8px', borderRadius: '10px' };
const title = { margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' };

const guideBody = { flex: 1 };
const infoBadge = { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', background: '#f1f5f9', borderRadius: '20px', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '15px' };
const subTitle = { margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#475569' };
const listStyle = { margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#334155', lineHeight: '1.8' };

const securityNote = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#94a3b8', fontWeight: '600' };
const dividerStyle = { width: '1px', background: '#f1f5f9', height: '80%', alignSelf: 'center' };

const uploadContainer = { width: '100%', maxWidth: '360px' };
const uploadHeader = { textAlign: 'center', marginBottom: '30px' };
const circleIcon = { width: '60px', height: '60px', background: '#fff', borderRadius: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 15px', boxShadow: '0 8px 15px rgba(0,0,0,0.03)' };
const hiddenInput = { position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' };
const statusBox = { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '12px', margin: '20px 0', fontSize: '12px', fontWeight: '600' };

export default ComboVehicleEntry;